import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, CheckCircle2, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSession } from '@/hooks/useSession';
import {
  approveSnapshot,
  createSnapshot,
  listSnapshots,
  revokeSnapshot,
  type Artifact,
  type ArtifactSnapshot,
} from '@/lib/api';
import { useFlushBeforeSnapshot } from './CollaborationContext';
import { ConfirmActionDialog } from './ConfirmActionDialog';

interface ArtifactSnapshotsPanelProps {
  artifact: Artifact;
}

const STATE_TONE: Record<ArtifactSnapshot['approvalState'], string> = {
  unapproved: 'bg-surface-elevated text-text-secondary',
  approved: 'bg-success/25 text-success',
  superseded: 'bg-[oklch(0.7_0.16_295/0.25)] text-[var(--node-prompt)]',
  revoked: 'bg-error/25 text-error',
};

function fmt(ts: string | null): string {
  if (!ts) return '-';
  return ts.slice(0, 19).replace('T', ' ');
}

export function ArtifactSnapshotsPanel({
  artifact,
}: ArtifactSnapshotsPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useSession();
  const flushBeforeSnapshot = useFlushBeforeSnapshot();
  const isOwnerOrAdmin = isAdmin || (user !== null && user.id === artifact.ownerId);

  const snapshotsQuery = useQuery<ArtifactSnapshot[]>({
    queryKey: ['artifact', artifact.id, 'snapshots'],
    queryFn: () => listSnapshots(artifact.id),
  });

  const [name, setName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ArtifactSnapshot | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      await flushBeforeSnapshot();
      return createSnapshot(artifact.id, { name: name.trim() });
    },
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'snapshots'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    },
    onError: err => {
      setCreateError(err.message);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (snapshotId: string) => approveSnapshot(snapshotId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'snapshots'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    },
    onError: err => {
      setApproveError(err.message);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (input: { snapshotId: string; reason: string }) =>
      revokeSnapshot(input.snapshotId, { reason: input.reason }),
    onSuccess: () => {
      setRevokeTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'snapshots'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    },
    onError: err => {
      setRevokeError(err.message);
    },
  });

  const snapshots = snapshotsQuery.data ?? [];
  const currentApproved = useMemo(
    () => snapshots.find(s => s.id === artifact.currentApprovedSnapshotId) ?? null,
    [snapshots, artifact.currentApprovedSnapshotId]
  );

  function handleCreate(): void {
    setCreateError(null);
    if (!name.trim()) {
      setCreateError('Snapshot name is required');
      return;
    }
    createMutation.mutate();
  }

  function handleApprove(snapshotId: string): void {
    setApproveError(null);
    approveMutation.mutate(snapshotId);
  }

  return (
    <div className="flex flex-col gap-4 text-sm">
      <div className="rounded-md border border-success/25 bg-success/10 p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs uppercase tracking-wide text-success">
            Current approved snapshot
          </span>
          {currentApproved && (
            <span className="rounded-md bg-success/25 px-2 py-0.5 font-mono text-xs text-success">
              {currentApproved.name}
            </span>
          )}
        </div>
        {currentApproved ? (
          <div className="mt-2 flex flex-col gap-1 text-xs text-text-secondary">
            <span>
              Approved by:{' '}
              <span className="font-mono">
                {currentApproved.approvedByLogin ?? currentApproved.approvedBy ?? '-'}
              </span>
            </span>
            <span>
              Approved at: <span className="font-mono">{fmt(currentApproved.approvedAt)}</span>
            </span>
            <span>
              Created at: <span className="font-mono">{fmt(currentApproved.createdAt)}</span>
            </span>
            <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-text-primary">
              {currentApproved.plainTextCache.slice(0, 280) || '(empty)'}
            </p>
          </div>
        ) : (
          <p className="mt-2 text-xs text-text-secondary">
            {artifact.currentApprovedSnapshotId === null
              ? 'No approved snapshot yet.'
              : 'Loading current approved snapshot.'}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1 border-t border-border pt-3">
        <span className="text-text-secondary">Create snapshot</span>
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={e => {
              setName(e.target.value);
            }}
            placeholder="e.g. v1, post-review-rev2"
            className="flex-1 rounded-md border border-border bg-surface px-2 py-1 text-sm"
          />
          <Button
            onClick={handleCreate}
            disabled={createMutation.isPending || !name.trim()}
            className="inline-flex items-center gap-1.5"
          >
            <Camera className="size-4" />
            {createMutation.isPending ? 'Saving…' : 'Snapshot'}
          </Button>
        </div>
        <span className="text-xs text-text-secondary">
          Anyone with edit access (team artifact) or owner/admin (private) can create. Approval is
          owner/admin only.
        </span>
        {createError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {createError}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-text-secondary">History</span>
        {snapshotsQuery.isLoading && (
          <span className="text-xs text-text-secondary">Loading snapshots…</span>
        )}
        {snapshotsQuery.isError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {snapshotsQuery.error.message}
          </div>
        )}
        {snapshots.length === 0 && !snapshotsQuery.isLoading && (
          <span className="text-xs text-text-secondary">No snapshots yet.</span>
        )}
        {snapshots.length > 0 && (
          <ul className="flex flex-col gap-2">
            {snapshots.map(s => {
              const isCurrent = s.id === artifact.currentApprovedSnapshotId;
              return (
                <li
                  key={s.id}
                  className="flex flex-col gap-1 rounded-md border border-border bg-surface/40 p-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-text-primary">{s.name}</span>
                    <span
                      className={`rounded-md px-2 py-0.5 font-mono text-xs ${STATE_TONE[s.approvalState]}`}
                    >
                      {s.approvalState}
                    </span>
                    {isCurrent && (
                      <span className="rounded-md bg-success px-2 py-0.5 font-mono text-xs text-success">
                        current
                      </span>
                    )}
                    <span className="ml-auto text-xs text-text-secondary">{fmt(s.createdAt)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {s.approvalState === 'unapproved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!isOwnerOrAdmin || approveMutation.isPending}
                        onClick={(): void => {
                          handleApprove(s.id);
                        }}
                        className="inline-flex items-center gap-1.5"
                      >
                        <CheckCircle2 className="size-4" />
                        Approve
                      </Button>
                    )}
                    {isCurrent && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!isOwnerOrAdmin || revokeMutation.isPending}
                        onClick={(): void => {
                          setRevokeError(null);
                          setRevokeTarget(s);
                        }}
                        className="inline-flex items-center gap-1.5 border-error/50 text-error hover:bg-error/15"
                      >
                        <ShieldOff className="size-4" />
                        Revoke approval
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {approveError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {approveError}
          </div>
        )}
      </div>

      <ConfirmActionDialog
        open={revokeTarget !== null}
        onOpenChange={next => {
          if (!next) setRevokeTarget(null);
        }}
        title="Revoke approval?"
        description={
          revokeTarget
            ? `This marks "${revokeTarget.name}" as revoked. The snapshot body stays intact. The current approved pointer falls back to the most recent restorable snapshot, or null if none.`
            : ''
        }
        confirmText="Revoke approval"
        confirmTone="danger"
        reasonLabel="Reason (required)"
        onConfirm={(reason): void => {
          if (revokeTarget && reason) {
            revokeMutation.mutate({ snapshotId: revokeTarget.id, reason });
          }
        }}
        pending={revokeMutation.isPending}
        errorMessage={revokeError}
      />
    </div>
  );
}
